import { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Mensaje de error */
  error?: string;
  /** Label del textarea */
  label?: string;
  /** Texto de ayuda debajo del textarea */
  helpText?: string;
}

/**
 * Textarea - Campo de texto multilínea con estilos Tailwind Plus
 * 
 * @example
 * ```tsx
 * // Básico con label
 * <Textarea label="Message" placeholder="Write your message..." />
 * 
 * // Con help text
 * <Textarea label="Bio" helpText="Write a few sentences about yourself." />
 * 
 * // Con error
 * <Textarea label="Message" error="Message is required." />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = '', error, label, helpText, id, disabled, ...props }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substring(2, 9)}`;
    const hasError = !!error;
    
    const describedBy = hasError 
      ? `${textareaId}-error` 
      : helpText 
        ? `${textareaId}-description` 
        : undefined;

    return (
      <div>
        {label && (
          <label 
            htmlFor={textareaId} 
            className="block text-sm/6 font-medium text-secondary-900 dark:text-white"
          >
            {label}
          </label>
        )}
        <div className={cn(label && 'mt-2')}>
          <textarea
            ref={ref}
            id={textareaId}
            disabled={disabled}
            className={cn(
              // Base
              'block w-full rounded-md bg-white px-3 py-1.5 text-base text-secondary-900 sm:text-sm/6',
              // Normal state - Light mode (usando border)
              !hasError && [
                'border border-secondary-300',
                'placeholder:text-secondary-400',
                'focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
              ],
              // Error state - Light mode
              hasError && [
                'text-danger-900 border border-danger-300',
                'placeholder:text-danger-300',
                'focus:border-danger-500 focus:ring-1 focus:ring-danger-500',
              ],
              // Normal state - Dark mode
              !hasError && [
                'dark:bg-white/5 dark:text-white dark:border-white/10',
                'dark:placeholder:text-secondary-500',
                'dark:focus:border-primary-500 dark:focus:ring-primary-500',
              ],
              // Error state - Dark mode
              hasError && [
                'dark:bg-danger-950/20 dark:text-danger-300 dark:border-danger-500/50',
                'dark:placeholder:text-danger-400',
                'dark:focus:border-danger-500 dark:focus:ring-danger-500',
              ],
              // Disabled state
              'disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-500 disabled:border-secondary-200',
              'dark:disabled:bg-white/[0.02] dark:disabled:text-secondary-500 dark:disabled:border-white/5',
              // Remove default focus outline
              'focus:outline-none',
              className
            )}
            aria-invalid={hasError ? 'true' : undefined}
            aria-describedby={describedBy}
            {...props}
          />
        </div>
        
        {/* Help text */}
        {helpText && !hasError && (
          <p 
            id={`${textareaId}-description`} 
            className="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
          >
            {helpText}
          </p>
        )}
        
        {/* Error message */}
        {hasError && (
          <p 
            id={`${textareaId}-error`} 
            className="mt-2 text-sm text-danger-600 dark:text-danger-400" 
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
